/** @jsx jsx */
import { css, jsx } from '@emotion/react';

import * as React from 'react';

import { Button, FullscreenDialog, IconButton, Typography2, breakpoints, useMediaQuery } from '@coursera/cds-core';

import CourseraLogo from 'bundles/page/components/CourseraLogo';

import _t from 'i18n!nls/authentication';

const { Content } = FullscreenDialog;

const FULLSCREEN_MODAL_HEADER_HEIGHT = {
  DESKTOP: 80,
  MOBILE: 55,
};

type Props = {
  isOpen: boolean;
  onAfterClose: () => void;
  contentLabel?: string;
  children: React.ReactNode;
};

const styles = {
  headerContainer: css`
    background-color: var(--cds-color-neutral-background-primary);
    box-shadow: 0 2px 2px var(--cds-color-darken-100);
    display: flex;
    height: ${FULLSCREEN_MODAL_HEADER_HEIGHT.DESKTOP}px;
    position: fixed;
    top: 0;
    left: 0;
    width: 100%;
    z-index: 1;

    ${breakpoints.down('xs')} {
      /* Mobile: only header height */
      height: ${FULLSCREEN_MODAL_HEADER_HEIGHT.MOBILE}px;
    }
  `,
  headerContent: css`
    display: flex;
    justify-content: space-between;
    width: 100%;
    padding: 0 var(--cds-spacing-600);

    ${breakpoints.down('xs')} {
      padding: 0;
      justify-content: center;
    }
  `,
  contentContainer: css`
    .authentication-box-content {
      height: 100%;
    }

    /* Add margin to content to avoid overlap with fixed header */

    /* Desktop: header + additional spacing */
    margin-top: ${FULLSCREEN_MODAL_HEADER_HEIGHT.DESKTOP + 80}px !important;

    ${breakpoints.down('xs')} {
      /* Mobile: only header height */
      margin-top: ${FULLSCREEN_MODAL_HEADER_HEIGHT.MOBILE}px !important;
    }
  `,
  logo: css`
    width: 108px;
  `,
  closeBtn: css`
    align-self: center;
    position: fixed;
    right: 12px;
    top: 8px;
    z-index: 10;
  `,
  exitButton: css`
    padding: var(--cds-spacing-300) var(--cds-spacing-100);
  `,
};

const FullScreenModal = ({ children, contentLabel = _t('Authentication Modal'), isOpen, onAfterClose }: Props) => {
  const isMobile = useMediaQuery(breakpoints.down('xs'));

  return (
    <FullscreenDialog open={isOpen} onClose={onAfterClose} contentWidth="60%" aria-label={contentLabel} role="dialog">
      <div css={styles.headerContainer}>
        <div css={styles.headerContent}>
          <CourseraLogo css={styles.logo} />
          {isMobile ? (
            <IconButton
              css={styles.closeBtn}
              intent="close"
              variant="ghostSecondary"
              size="small"
              onClick={onAfterClose}
              aria-label={_t('Close modal')}
            />
          ) : (
            <Button
              css={styles.exitButton}
              aria-label={_t('Close modal')}
              onClick={onAfterClose}
              variant="ghostSecondary"
              size="small"
            >
              <Typography2 variant="subtitleMedium" component="span">
                {_t('Exit')}
              </Typography2>
            </Button>
          )}
        </div>
      </div>
      <Content css={styles.contentContainer}>{children}</Content>
    </FullscreenDialog>
  );
};

export default FullScreenModal;
